package com.klantenstop.web.pages.manageCompany.create

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.klantenstop.web.components.layout.PageLayout
import com.klantenstop.web.components.CategoryRequestHelper
import com.klantenstop.web.data.models.domain.Status
import com.klantenstop.web.navigation.Navigation
import com.klantenstop.web.pages.manageCompany.components.AddressInputSection
import com.klantenstop.web.pages.manageCompany.components.GuidanceCard
import com.materialkobweb.utils.rememberViewModel
import com.klantenstop.web.components.CustomSelect
import com.materialkobweb.components.widgets.DsBorderRadius
import com.materialkobweb.components.widgets.DsEditableArea
import com.materialkobweb.components.widgets.DsEditableField
import com.materialkobweb.components.widgets.FilledButton
import com.materialkobweb.components.widgets.TextButton
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxHeight
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.materialkobweb.styles.MaterialColorVars
import com.materialkobweb.styles.withAlpha
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text

@Composable
fun CreateCompanyScreen() {
    val ctx = rememberPageContext()

    // Use rememberViewModel with cached=false to ensure proper disposal and always start from a clean slate
    val viewModel = rememberViewModel(cached = false) { CreateCompanyViewModel() }
    val state by viewModel.state.collectAsState()

    LaunchedEffect(Unit) {
        viewModel.effect.collect { effect ->
            when (effect) {
                is CreateCompanyContract.Effect.NavigateBack -> {
                    if (effect.companyIdentifier != null) {
                        val route = Navigation.Screen.CompanyDetail(effect.companyIdentifier).route
                        ctx.router.navigateTo(route, updateHistoryMode = UpdateHistoryMode.REPLACE)
                    } else {
                        window.history.back()
                    }
                }
            }
        }
    }

    CreateCompanyScreenContent(state, viewModel::sendIntent)
}

@Composable
private fun CompanyFormFields(
    state: CreateCompanyContract.State,
    sendIntent: (CreateCompanyContract.Intent) -> Unit
) {
    // Establishment name with guidance
//    GuidanceCard("💡 Tip: Voeg de locatie toe aan de bedrijfsnaam om vestigingen te onderscheiden (bijv. \"Dr. Janssen - Leuven Centrum\")")

    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyName",
        label = "Bedrijfsnaam",
        placeholder = "Bijv. Dr. Janssen Tandartspraktijk",
        value = state.name,
        valid = !state.hasAttemptedSave || state.isNameValid,
        onValueChange = { sendIntent(CreateCompanyContract.Intent.UpdateName(it)) },
        required = true,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    HorizontalDivider(modifier = Modifier.fillMaxWidth().margin(topBottom = 12.px))

    // Address input section
    AddressInputSection(
        streetName = state.streetName,
        streetNumber = state.streetNumber,
        zipCode = state.zipCode,
        city = state.city,
        country = state.country,
        onStreetNameChange = { sendIntent(CreateCompanyContract.Intent.UpdateStreetName(it)) },
        onStreetNumberChange = { sendIntent(CreateCompanyContract.Intent.UpdateStreetNumber(it)) },
        onZipCodeChange = { sendIntent(CreateCompanyContract.Intent.UpdateZipCode(it)) },
        onCityChange = { sendIntent(CreateCompanyContract.Intent.UpdateCity(it)) },
        onCountryChange = { sendIntent(CreateCompanyContract.Intent.UpdateCountry(it)) },
        onVerifyAddress = { sendIntent(CreateCompanyContract.Intent.ValidateAddress) },
        isValidating = state.isValidatingAddress,
        validationResult = state.addressValidationResult,
        hasAttemptedSave = state.hasAttemptedSave,
        isAddressValidated = state.isAddressValidated
    )

    HorizontalDivider(modifier = Modifier.fillMaxWidth().margin(topBottom = 12.px))

    // Category selection
    Column(
        modifier = Modifier.fillMaxWidth()
    ) {
        CustomSelect(
            modifier = Modifier.fillMaxWidth(),
            id = "dropdown-create-category",
            label = "Categorie",
            placeholder = "Selecteer een categorie",
            items = state.categories.map { it.name },
            selectedItem = state.selectedCategory?.name,
            valid = !state.hasAttemptedSave || state.isCategoryValid,
            onItemSelect = { index, _ ->
                sendIntent(CreateCompanyContract.Intent.UpdateCategory(state.categories.getOrNull(index)))
            }
        )
        CategoryRequestHelper(source = "create_company_category_dropdown")
    }

    // Company Description
    DsEditableArea(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyDescription",
        label = "Bedrijf beschrijving",
        placeholder = "",
        value = state.companyDescription,
        onValueChange = {
            sendIntent(CreateCompanyContract.Intent.UpdateDescription(it))
        }
    )

    // Optional fields
    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyPhoneNumber",
        label = "Telefoonnummer",
        placeholder = "Telefoonnummer",
        value = state.phoneNumber,
        valid = !state.hasAttemptedSave || state.isPhoneNumberValid,
        onValueChange = { sendIntent(CreateCompanyContract.Intent.UpdatePhoneNumber(it)) },
        required = false,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyEmail",
        label = "E-mailadres",
        placeholder = "E-mailadres",
        value = state.emailAddress,
        valid = !state.hasAttemptedSave || state.isEmailAddressValid,
        onValueChange = { sendIntent(CreateCompanyContract.Intent.UpdateEmailAddress(it)) },
        required = false,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    DsEditableField(
        modifier = Modifier.fillMaxWidth(),
        id = "inputCompanyWebsite",
        label = "Website",
        placeholder = "Website",
        value = state.website,
        valid = !state.hasAttemptedSave || state.isWebsiteValid,
        onValueChange = { sendIntent(CreateCompanyContract.Intent.UpdateWebsite(it)) },
        required = false,
        backgroundColor = MaterialColorVars.SurfaceContainer.value(),
        focusBorderColor = MaterialColorVars.Primary.withAlpha(0.6f)
    )

    HorizontalDivider(modifier = Modifier.fillMaxWidth().margin(topBottom = 12.px))

    CustomSelect(
        modifier = Modifier.fillMaxWidth(),
        id = "dropdown-create-status",
        label = "Status",
        placeholder = "",
        items = Status.Type.entries.map { it.toText() },
        selectedItem = state.selectedStatusType.toText(),
        onItemSelect = { _, item ->
            sendIntent(CreateCompanyContract.Intent.UpdateCompanyStatus(Status.Type.fromText(item)))
        }
    )

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.px)
    ) {
        CheckboxInput(checked = state.showStatusDescription) {
            id("checkboxStatusDescription")
            onChange { sendIntent(CreateCompanyContract.Intent.ToggleStatusDescriptionVisibility) }
        }
        Label(forId = "checkboxStatusDescription") {
            Text("Voeg extra beschrijving toe")
        }
    }

    if (state.showStatusDescription) {
        GuidanceCard("💡 Tip: Gebruik de statusbeschrijving om extra context te geven over de huidige status van het bedrijf (bijv. \"Tijdelijk gesloten wegens renovatie, heropening gepland voor Q4 2024\")")

        DsEditableArea(
            modifier = Modifier.fillMaxWidth(),
            id = "inputStatusDescription",
            label = "Status beschrijving",
            placeholder = "",
            value = state.statusDescription,
            onValueChange = {
                sendIntent(CreateCompanyContract.Intent.UpdateCompanyStatusDescription(it))
            }
        )
    }
}

@Composable
private fun CreateCompanyScreenContent(
    state: CreateCompanyContract.State,
    sendIntent: (CreateCompanyContract.Intent) -> Unit
) {
    val breakpoint = rememberBreakpoint()

    PageLayout(
        modifier = Modifier.fillMaxSize(),
        title = "Nieuw bedrijf aanmaken"
    ) {
        Column(
            modifier = Modifier
                .maxWidth(49.cssRem)
                .fillMaxWidth(
                    when {
                        breakpoint < Breakpoint.SM -> 90.percent
                        breakpoint == Breakpoint.LG -> 60.percent
                        breakpoint >= Breakpoint.XL -> 40.percent
                        else -> 70.percent
                    }
                )
                .fillMaxHeight()
                .padding(topBottom = if (breakpoint >= Breakpoint.MD) 50.px else 20.px),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.px)
        ) {
            // Form fields
            CompanyFormFields(state, sendIntent)

            // Action buttons
            Row(
                horizontalArrangement = Arrangement.spacedBy(16.px)
            ) {
                FilledButton(
                    enabled = state.hasChanged && !state.callOnRoute,
                    onClick = { sendIntent(CreateCompanyContract.Intent.Save) }
                ) {
                    Text("Opslaan")
                }

                TextButton(
                    borderRadius = DsBorderRadius(8.px),
                    onClick = { sendIntent(CreateCompanyContract.Intent.Cancel) }
                ) {
                    Text("Annuleren")
                }
            }
        }
    }
}
